import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Toolbar,
    Typography,
    Button,
    Container,
    Paper,
    Chip
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Email as EmailIcon,
    Description as TemplateIcon,
    People as PeopleIcon,
    Settings as SettingsIcon,
    History as HistoryIcon,
    Assessment as ReportsIcon,
    School as LessonsIcon,
    Phone as PhoneIcon,
    Tv as TvIcon,
    LocalOffer as OffersIcon,
    BarChart as PerformanceIcon,
    SentimentVeryDissatisfied as SadIcon,
    SmartToy as AIIcon,
    Quiz as QuizIcon,
    Forum as EscalationsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import RouteErrorBoundary from './common/RouteErrorBoundary';
import AgentPolicyGateDialog from './common/AgentPolicyGateDialog';
import PortalBreadcrumbs from './shell/PortalBreadcrumbs';
import PortalHeader from './shell/PortalHeader';
import PortalSidebar, { PORTAL_DRAWER_WIDTH } from './shell/PortalSidebar';
import useAgentPolicyGate from '../hooks/useAgentPolicyGate';
import useEscalationsShellState from '../hooks/useEscalationsShellState';
import '../visualRefreshPreview.css';
import {
    getPortalBreadcrumbs,
    getPortalPathForView,
    getPortalViewFromPath
} from '../navigation/portalRoutes';
import {
    AGENT_ACCESSIBLE_VIEWS,
    PROCESSING_ACCESSIBLE_VIEWS
} from '../navigation/accessRules';
import {
    ADMIN_MENU_GROUP_DEFINITIONS,
    AGENT_MENU_GROUP_DEFINITIONS,
    materializeMenuGroups,
    materializeProcessingMenu
} from '../navigation/portalNavigation';

// Import placeholder components (to be implemented)
import AgentDashboard from './AgentDashboard';
import AdminDashboard from './AdminDashboard';

const adminViewModeStorageKey = 'adminViewMode';
const navigationIcons = {
    dashboard: <DashboardIcon />,
    email: <EmailIcon />,
    template: <TemplateIcon />,
    people: <PeopleIcon />,
    settings: <SettingsIcon />,
    history: <HistoryIcon />,
    reports: <ReportsIcon />,
    lessons: <LessonsIcon />,
    phone: <PhoneIcon />,
    tv: <TvIcon />,
    offers: <OffersIcon />,
    performance: <PerformanceIcon />,
    sad: <SadIcon />,
    ai: <AIIcon />,
    quiz: <QuizIcon />,
    escalations: <EscalationsIcon />
};

const getStoredAdminViewMode = () => {
    try {
        return sessionStorage.getItem(adminViewModeStorageKey) === 'agent' ? 'agent' : 'admin';
    } catch (error) {
        return 'admin';
    }
};

function Dashboard() {
    const { currentUser, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isActualAdmin = Boolean(currentUser?.isAdmin);
    const isProcessing = currentUser?.accessProfile === 'processing';
    const previewParams = new URLSearchParams(location.search);
    const isVisualRefreshPreview = previewParams.get('visualRefreshPreview') === '1';
    const isVisualRefreshAgentMode = isVisualRefreshPreview && previewParams.get('agentViewMode') === '1';
    const [adminViewMode, setAdminViewMode] = useState(() => (
        isVisualRefreshAgentMode ? 'agent' : getStoredAdminViewMode()
    ));
    const isAgentViewMode = isActualAdmin && adminViewMode === 'agent';
    const effectiveIsAdmin = isActualAdmin && !isAgentViewMode;
    
    // Get current view from URL
    const getCurrentView = () => {
        return getPortalViewFromPath(location.pathname, { isActualAdmin });
    };
    
    const [selectedView, setSelectedView] = useState(getCurrentView);
    const [mobileOpen, setMobileOpen] = useState(false);
    const policyGate = useAgentPolicyGate({ currentUser, isActualAdmin, isProcessing });
    const {
        availability: escalationsAvailability,
        setAvailability: setEscalationsAvailability,
        availabilityLoaded: escalationsAvailabilityLoaded,
        unreadCount: escalationUnreadCount
    } = useEscalationsShellState({ currentUser, isActualAdmin, isProcessing });
    const defaultExpandedGroups = {
        quoteTools: true,
        resources: true,
        history: true,
        offersEmail: true,
        productPricing: true,
        training: true,
        performance: true,
        admin: true,
        legacyAdvanced: true,
        offers: true,
        users: true,
        comingSoon: true
    };

    const [expandedGroups, setExpandedGroups] = useState(() => {
        // Load expanded state from localStorage
        const saved = localStorage.getItem('navExpandedGroups');
        return saved ? { ...defaultExpandedGroups, ...JSON.parse(saved) } : defaultExpandedGroups;
    });

    useEffect(() => {
        if (location.pathname.startsWith('/coming-soon') && !isActualAdmin) {
            navigate('/dashboard', { replace: true });
        }
    }, [isActualAdmin, location.pathname, navigate]);

    // Update view when URL changes
    useEffect(() => {
        const newView = getCurrentView();
        if (newView !== selectedView) {
            setSelectedView(newView);
        }
    }, [isActualAdmin, location.pathname, selectedView]);

    useEffect(() => {
        if (!isActualAdmin && adminViewMode !== 'admin') {
            setAdminViewMode('admin');
        }
    }, [adminViewMode, isActualAdmin]);

    useEffect(() => {
        if (isVisualRefreshAgentMode && adminViewMode !== 'agent') {
            setAdminViewMode('agent');
        }
    }, [adminViewMode, isVisualRefreshAgentMode]);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const toggleGroup = (group) => {
        setExpandedGroups(prev => {
            const newState = {
                ...prev,
                [group]: !prev[group]
            };
            // Save to localStorage
            localStorage.setItem('navExpandedGroups', JSON.stringify(newState));
            return newState;
        });
    };

    // Navigation function that updates URL
    const navigateToView = (viewId) => {
        const baseUrl = getPortalPathForView(viewId);
        const url = isVisualRefreshPreview ? `${baseUrl}${location.search}` : baseUrl;
        navigate(url);
        // Remove the setSelectedView call since useEffect will handle it
    };

    const switchAdminViewMode = (mode) => {
        const nextMode = mode === 'agent' ? 'agent' : 'admin';
        setAdminViewMode(nextMode);
        try {
            sessionStorage.setItem(adminViewModeStorageKey, nextMode);
        } catch (error) {
            // Session persistence is a convenience only; the view still switches without it.
        }
    };

    const adminMenuGroups = materializeMenuGroups(ADMIN_MENU_GROUP_DEFINITIONS, navigationIcons);
    const agentMenuGroups = materializeMenuGroups(AGENT_MENU_GROUP_DEFINITIONS, navigationIcons);
    const processingMenuItems = materializeProcessingMenu(navigationIcons);

    const menuGroups = effectiveIsAdmin ? adminMenuGroups : agentMenuGroups;
    const escalationAccessVisible = Boolean(isActualAdmin || escalationsAvailability?.availableToCurrentUser);
    const isMenuItemVisible = (item) => {
        const roleVisible = item.forAll ||
            (item.adminOnly && effectiveIsAdmin) ||
            (item.agentOnly && !effectiveIsAdmin);
        return roleVisible && (item.id !== 'escalations' || escalationAccessVisible);
    };
    const renderMenuItemLabel = (item) => {
        if (item.id !== 'escalations' || escalationUnreadCount < 1) {
            return item.label;
        }
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <span>{item.label}</span>
                <Chip
                    component="span"
                    size="small"
                    color="error"
                    label={escalationUnreadCount > 99 ? '99+' : escalationUnreadCount}
                    sx={{ height: 20, minWidth: 28, '& .MuiChip-label': { px: 0.75 } }}
                />
            </Box>
        );
    };

    const renderAgentViewAccessPrompt = () => (
        <Paper sx={{ p: 3, maxWidth: 720 }}>
            <Typography variant="h5" gutterBottom>
                Agent View Mode
            </Typography>
            <Typography variant="body1" sx={{ mb: 1.5 }}>
                You are in Agent View. Switch to Admin View to access this page.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Agent View shows the agent experience using your admin account. It does not impersonate another user.
            </Typography>
            <Button variant="contained" onClick={() => switchAdminViewMode('admin')}>
                Switch to Admin View
            </Button>
        </Paper>
    );

    const renderContent = () => {
        if (isProcessing) {
            return (
                <AgentDashboard
                    selectedView={PROCESSING_ACCESSIBLE_VIEWS.has(selectedView) ? selectedView : 'bundle-builder-beta'}
                    escalationsAvailability={null}
                    escalationsAvailabilityLoaded
                />
            );
        }

        if (isAgentViewMode) {
            if (!AGENT_ACCESSIBLE_VIEWS.has(selectedView)) {
                return renderAgentViewAccessPrompt();
            }

            return (
                <AgentDashboard
                    selectedView={selectedView}
                    agentViewMode
                    escalationsAvailability={escalationsAvailability}
                    escalationsAvailabilityLoaded={escalationsAvailabilityLoaded}
                    onEscalationsAvailabilityChange={setEscalationsAvailability}
                />
            );
        }

        if (isActualAdmin) {
            return (
                <AdminDashboard
                    selectedView={selectedView}
                    setSelectedView={navigateToView}
                    escalationsAvailability={escalationsAvailability}
                    escalationsAvailabilityLoaded={escalationsAvailabilityLoaded}
                    onEscalationsAvailabilityChange={setEscalationsAvailability}
                />
            );
        }

        return (
            <AgentDashboard
                selectedView={selectedView}
                escalationsAvailability={escalationsAvailability}
                escalationsAvailabilityLoaded={escalationsAvailabilityLoaded}
                onEscalationsAvailabilityChange={setEscalationsAvailability}
            />
        );
    };

    const breadcrumbs = getPortalBreadcrumbs(location.pathname, { effectiveIsAdmin });
    const currentPageName = breadcrumbs[breadcrumbs.length - 1]?.label || 'Dashboard';
    const visualRefreshClassName = `visual-refresh-preview visual-refresh-view-${selectedView}`;

    return (
        <Box
            className={visualRefreshClassName}
            sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}
        >
            <PortalHeader
                currentUser={currentUser}
                isActualAdmin={isActualAdmin}
                isAgentViewMode={isAgentViewMode}
                onToggleMobileNavigation={() => setMobileOpen((open) => !open)}
                onSwitchAdminViewMode={switchAdminViewMode}
                onLogout={handleLogout}
            />

            <PortalSidebar
                mobileOpen={mobileOpen}
                onCloseMobile={() => setMobileOpen(false)}
                onNavigate={navigateToView}
                effectiveIsAdmin={effectiveIsAdmin}
                expandedGroups={expandedGroups}
                isMenuItemVisible={isMenuItemVisible}
                isProcessing={isProcessing}
                isVisualRefreshPreview={isVisualRefreshPreview}
                locationSearch={location.search}
                menuGroups={menuGroups}
                onToggleGroup={toggleGroup}
                processingMenuItems={processingMenuItems}
                renderMenuItemLabel={renderMenuItemLabel}
                selectedView={selectedView}
            />

            <Box 
                component="main" 
                sx={{ 
                    flexGrow: 1, 
                    p: 3, 
                    width: { sm: `calc(100% - ${PORTAL_DRAWER_WIDTH}px)` },
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    height: '100vh'
                }}
            >
                <Toolbar />
                <Container maxWidth={selectedView === 'promo-combo-price' || selectedView === 'rogers-pricing' || selectedView === 'bundle-builder' || selectedView === 'bundle-builder-beta' || selectedView === 'users' ? false : 'lg'}>
                    <PortalBreadcrumbs breadcrumbs={breadcrumbs} />

                    {policyGate.warning && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {policyGate.warning}
                        </Alert>
                    )}

                    <RouteErrorBoundary
                        resetKey={`${location.pathname}:${selectedView}:${adminViewMode}`}
                        pageName={currentPageName}
                        showAdminDetails={isActualAdmin}
                    >
                        {renderContent()}
                    </RouteErrorBoundary>
                </Container>
            </Box>

            <AgentPolicyGateDialog policyGate={policyGate} />
        </Box>
    );
}

export default Dashboard;
